import { computed, inject, Injectable } from '@angular/core';
import { TelemetryResponse } from '@key-telematics/fleet-api-client/lib/model';
import { AppService } from 'app/app.service';



export interface IconHtmlOptions {
    fill?: string;
    secondaryColorFill?: string;
    strokeFill?: string;
    rotation?: string | number;
    size?: number;
    dropShadowAlpha?: number;
}

/*
* these are the available color presets returned from the payload
*/
export enum MAP_COLORS_ENUM {
    'blue',
    'lightblue',
    'green',
    'olive',
    'orange',
    'pink',
    'purple',
    'red',
    'teal',
    'yellow',
    'lightgray',
    'gray',
}

/*
* these are the better display color substitues for the available color presets
 * Note: Most of these are copics, or slight adjusted ones for usability.
*/
export const MAP_COLORS: string[] = [
    '#058FD0', // blue
    '#62CBE8', // lightblue
    '#72C156', // green
    '#98A786', // olive
    '#F26B3C', // orange
    '#CE218E', // pink
    '#9721CE', // purple
    '#EF5151', // red
    '#01B1C9', // teal
    '#FFE455', // yellow
    '#999', // light gray
    '#666', // gray
];

export type IconType = 'stop' | 'idle' | 'direction' | string;

@Injectable({ providedIn: 'root' })
export class MapMarkersService {

    private app = inject(AppService);

    private readonly iconSet = computed(() => this.app.theme?.settings?.icons?.location?.set || 'default');

    public isBLEIcon(asset: any): boolean {

        if (asset?.devices?.length > 0) {
            return asset.devices.some(d => d.deviceType.name === 'MatrixIQ' || d.deviceType.name === 'Vital');
        }

        return false;
    }

    public getTelemetryIconType(telemetry: TelemetryResponse, hasTrips: boolean = true): string {
        if (telemetry) {
            const isActive = telemetry.active;
            const speed = telemetry.location && telemetry.location.speed;

            if (isActive) {
                return (speed === 0) ? 'stop' : 'direction';
            }
        }

        if (this.iconSet() === 'kinesis' && !hasTrips) {
            return 'non-ignition';
        }

        return 'idle';
    }

    public getIconSvgString(iconType?: IconType, options?: IconHtmlOptions, hasTrips: boolean = true): string {
        switch (this.iconSet()) {
            case 'kinesis':
                return this.getRadiusTripIconSvgString(iconType, options, hasTrips);
            default:
                return this.getKeyIconSvgString(iconType, options);
        }
    }

    public getTripIconSvgString(iconType?: IconType, options?: IconHtmlOptions, hasTrips: boolean = true): string {
        switch (this.iconSet()) {
            case 'kinesis':
                return this.getRadiusTripIconSvgString(iconType, options, hasTrips);
            default:
                return this.getKeyIconSvgString(iconType, options);
        }
    }

    public getStartIcon(size = 50): string {
        return `<svg width="${size}" height="${size}" viewBox="-3 -3 30 30" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
            <g id="Frame 1000001492">
            <rect width="24" height="24" rx="12" fill="#101920"/>
            <circle id="Ellipse 98" cx="12" cy="12" r="8.8" fill="#00BE73"/>
            </g>
        </svg>`;
    }

    public getEndIcon(size = 50): string {
        return `<svg preserveAspectRatio="none" overflow="visible" style="display: block;" width="${size}" height="${size}" viewBox="-3 -3 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g id="Frame 1000001473">
            <circle id="Ellipse 94" cx="12" cy="12" r="12" fill="#101920"/>
            <path id="Combined Shape" d="M16.4621 18.7416C16.2099 18.9072 15.9509 19.0637 15.6857 19.2064C15.4339 19.3427 15.1751 19.4701 14.9094 19.5756C14.6433 19.6816 14.3671 19.7622 14.092 19.8432C13.899 19.8997 13.7016 19.9432 13.5041 19.9818C13.3063 20.0204 13.1067 20.0466 12.9074 20.0756C12.607 20.1193 12.3049 20.1331 12.0031 20.1303V16.433H16.4621V18.7416ZM7.54512 18.7309C7.32451 18.5774 7.10638 18.4192 6.89766 18.2504C6.44457 17.8845 6.04816 17.4566 5.69355 16.9926C5.55494 16.8109 5.42124 16.6248 5.29707 16.433H7.54512V18.7309ZM12.0031 16.433H7.54512V11.9936H12.0031V16.433ZM19.9963 11.9936C19.9958 12.2408 19.9885 12.4886 19.968 12.7367C19.939 13.0812 19.8704 13.4179 19.8088 13.7562C19.7306 14.1874 19.5958 14.6011 19.4357 15.0053C19.237 15.506 18.9905 15.9813 18.7004 16.433H16.4621V11.9936H19.9963ZM16.4621 11.9936H12.0031V7.55312H16.4621V11.9936ZM7.54512 11.9936H4.00801C4.00753 11.9872 4.0076 11.9804 4.00703 11.974C4.01778 11.6903 4.02635 11.4063 4.04121 11.1225C4.04575 11.0364 4.06836 10.9511 4.08223 10.8656C4.10838 10.7027 4.13089 10.5385 4.1623 10.3764C4.19223 10.2219 4.22201 10.0653 4.26875 9.91543C4.36883 9.59257 4.46435 9.26602 4.59102 8.95352C4.72534 8.62165 4.87931 8.296 5.05 7.98184C5.12951 7.8353 5.2144 7.69277 5.30293 7.55312H7.54512V11.9936ZM12.0031 3.85488V7.55312H7.54512V5.24258C7.62138 5.19156 7.69776 5.1398 7.77656 5.09219C8.01605 4.94787 8.25642 4.80337 8.50605 4.6791C8.75118 4.55756 9.00506 4.45072 9.26191 4.35781C9.55556 4.25117 9.85498 4.15877 10.1555 4.07461C10.3442 4.02168 10.5392 3.98846 10.7326 3.95742C11.1541 3.89008 11.578 3.85232 12.0031 3.85488ZM16.4621 5.24453C16.7538 5.44404 17.034 5.66103 17.299 5.90078C17.6285 6.19929 17.9409 6.51512 18.2102 6.86855C18.379 7.09025 18.5406 7.31893 18.6936 7.55312H16.4621V5.24453Z" fill="white"/>
            </g>
        </svg>`;
    }

    private getKeyIconSvgString(type?: string, options?: IconHtmlOptions): string {
        options = {
            fill: '#333333',
            secondaryColorFill: '#ffffff',
            strokeFill: '#ffffff',
            size: 50,
            rotation: 0,
            dropShadowAlpha: 0,
            ...options,
        };

        switch (type) {
            case 'stop':
                /* tslint:disable */
                return `<svg id="stop" width="${options.size}" height="${options.size}" viewBox="0 0 30 30" version="1.1">
                    <g
                        transform="translate(12,6)"
                        style="fill:none;fill-rule:evenodd">
                        <circle
                            style="fill:${options.fill};stroke:${options.strokeFill};stroke-width:1;stroke-location:outside;stroke-miterlimit:4;stroke-dasharray:none"
                            cx="3"
                            cy="9"
                            r="10" />
                        <g transform="matrix(1.2435685,0,0,0.67151445,-2.4268539,7.4173964)" style="fill-rule:nonzero" />
                        <ellipse
                            style="fill:${options.secondaryColorFill};stroke:${options.secondaryColorFill};stroke-width:6.219;stroke-miterlimit:4;stroke-dasharray:none;stroke-opacity:1;paint-order:stroke markers fill"
                            cx="2.9499993"
                            cy="9.0500002"
                            rx="5.2317243"
                            ry="5.2317247" />
                        <g
                            aria-label="S"
                            style="font-style:normal;font-weight:normal;font-size:40px;line-height:1.25;font-family:sans-serif;letter-spacing:0px;word-spacing:0px;fill:${options.fill};fill-opacity:1;stroke:none">
                        <path
                            d="M 5.0888777,6.8408443 Q 4.7955444,6.467511 4.3155444,6.2541777 3.8488777,6.027511 3.4088777,6.027511 q -0.2266666,0 -0.4666666,0.04 -0.2266667,0.04 -0.4133334,0.16 -0.1866666,0.1066667 -0.32,0.2933333 -0.12,0.1733334 -0.12,0.4533334 0,0.24 0.093333,0.4 0.1066666,0.16 0.2933333,0.2799999 0.2,0.12 0.4666667,0.2266667 0.2666666,0.093333 0.5999999,0.2 0.48,0.16 1,0.36 0.52,0.1866667 0.9466667,0.5066667 0.4266666,0.3199999 0.7066666,0.7999999 0.28,0.4666671 0.28,1.1733331 0,0.813334 -0.3066666,1.413334 -0.2933334,0.586666 -0.8,0.973333 -0.5066667,0.386667 -1.16,0.573333 -0.6533333,0.186667 -1.3466666,0.186667 -1.0133333,0 -1.95999999,-0.346667 -0.94666664,-0.36 -1.5733333,-1.013333 l 1.4933333,-1.52 q 0.34666669,0.426667 0.90666669,0.72 0.5733333,0.28 1.1333333,0.28 0.2533333,0 0.4933333,-0.05333 0.24,-0.05333 0.4133333,-0.173334 0.1866667,-0.12 0.2933333,-0.32 0.1066667,-0.2 0.1066667,-0.48 0,-0.266666 -0.1333333,-0.453333 -0.1333334,-0.186667 -0.3866667,-0.333333 -0.24,-0.16 -0.6133333,-0.28 -0.36,-0.1333337 -0.8266667,-0.2800004 -0.4533333,-0.1466667 -0.8933333,-0.3466667 -0.42666662,-0.2 -0.77333328,-0.5066666 -0.33333333,-0.32 -0.54666666,-0.76 -0.19999999,-0.4533333 -0.19999999,-1.0933333 0,-0.7866667 0.31999999,-1.3466666 0.31999999,-0.56 0.83999998,-0.92 0.51999996,-0.36 1.17333326,-0.52 0.6533334,-0.1733333 1.32,-0.1733333 0.8,0 1.6266666,0.2933333 0.84,0.2933333 1.4666667,0.8666666 z"
                            style="font-style:normal;font-variant:normal;font-weight:bold;font-stretch:normal;font-size:13.33333302px;font-family:'Avenir Next';-inkscape-font-specification:'Avenir Next Bold';fill:${options.fill}" />
                        </g>
                </g>
                </svg>`;
            /* tslint:enable */

            case 'idle':
                /* tslint:disable */
                return `<svg id="idle" width="${options.size}" height="${options.size}" viewBox="0 0 30 30" version="1.1">
                    <g
                        transform="translate(12,6)"
                        style="fill:none;fill-rule:evenodd">
                        <circle
                            style="fill:${options.fill};stroke-width:1;stroke:${options.strokeFill};stroke-location:outside;stroke-miterlimit:4;stroke-dasharray:none"
                            cx="3"
                            cy="9"
                            r="10" />
                        <circle style="fill:${options.secondaryColorFill};" cx="3" cy="9" r="8" />
                        <circle style="fill:${options.fill};" cx="3" cy="9" r="5" />
                </g>
                </svg>`;
            /* tslint:enable */

            case 'direction':
                /* tslint:disable */
                return `<svg id="direction" width="${options.size}" height="${options.size}" style="transform:rotate(${options.rotation}deg)" viewBox="0 0 30 30" version="1.1">
                    <g
                        transform="translate(12,6)"
                        style="fill:none;fill-rule:evenodd">
                        <path
                            style="fill:${options.fill};stroke:${options.strokeFill};stroke-width:1;stroke-location:outside;stroke-miterlimit:4;stroke-dasharray:none;stroke-opacity:1;paint-order:stroke markers fill"
                            d="M 15,0.30444516 C 15,0.30444516 5,8.1513272 5,15 5,20.522857 9.4771523,25 15,25 20.522857,25 25,20.522857 25,15 25,7.9745504 15,0.30444516 15,0.30444516 Z M 15,7.5 c 4.142167,0 7.5,3.992632 7.5,8.134766 C 22.5,19.776933 19.142167,22.5 15,22.5 10.857867,22.5 7.5,19.142167 7.5,15 7.5,10.857867 10.857867,7.5 15,7.5 Z"
                            transform="translate(-12,-6)" />

                        <g transform="matrix(1.2435685,0,0,0.67151445,-2.4268539,7.4173964)" style="fill-rule:nonzero" />
                        <circle style="fill:${options.secondaryColorFill};" cx="3" cy="9" r="8" />
                        <circle style="fill:${options.fill};" cx="3" cy="9" r="5" />
                    </g>
                </svg>`;
            /* tslint:enable */

            case 'gemini-x':
                /* tslint:disable */
                return `<svg width="${options.size}" height="${options.size}" viewBox="-8 -8 40 40" version="1.1" xmlns="http://www.w3.org/2000/svg">
                        <g clip-path="url(#clip0_1699_4964)">
                            <path d="M0 3.6C0 1.61178 1.61178 0 3.6 0H20.4C22.3882 0 24 1.61178 24 3.6V20.4C24 22.3882 22.3882 24 20.4 24H3.6C1.61178 24 0 22.3882 0 20.4V3.6Z" fill="#FC008E" />
                            <path d="M9.05318 6.59961L6.57163 6.60213C6.08333 6.60263 5.82082 7.17273 6.13948 7.54062L11.9814 14.2851L14.5083 17.2023C14.6168 17.3276 14.7748 17.3996 14.9411 17.3996H17.4278C17.9164 17.3996 18.1794 16.8291 17.8604 16.461L9.48643 6.79682C9.3778 6.67145 9.21959 6.59944 9.05318 6.59961Z" fill="white" />
                            <path d="M17.8605 7.54062C18.1792 7.17273 17.9167 6.60263 17.4284 6.60213L14.9468 6.59961C14.7804 6.59944 14.6222 6.67146 14.5136 6.79682L10 12.0058L12.0186 14.2851L17.8605 7.54062Z" fill="white" />
                            <path d="M11 15.4613L8.99 13.1719L6.13961 16.4611C5.82067 16.8292 6.08365 17.3997 6.57228 17.3997H9.05897C9.22522 17.3997 9.38321 17.3276 9.49171 17.2024L11 15.4613Z" fill="white" />
                        </g>
                        <defs>
                            <clipPath id="clip0_1699_4964">
                                <rect width="24" height="24" fill="white" />
                            </clipPath>
                        </defs>
                    </svg>`;
            /* tslint:enable */

            default:
                return `<svg></svg>`;
        }
    }

    private getRadiusTripIconSvgString(type?: string, options?: IconHtmlOptions, hasTrips: boolean = true): string {
        options = {
            fill: '#333333',
            secondaryColorFill: '#ffffff',
            strokeFill: '#ffffff',
            size: 50,
            rotation: 0,
            dropShadowAlpha: 0,
            ...options,
        };

        let updatedType = type;

        if (!hasTrips) {
            // gemini-x has a specific icon, use that instead.
            if (type !== 'gemini-x') {
                updatedType = 'non-ignition';
            }
        }

        switch (updatedType) {
            case 'stop':
                /* tslint:disable */
                return `<svg width="${options.size}" height="${options.size}" viewBox="0 0 25 25" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
                        <title>pin / trip-history / idling</title>
                        <g id="pin-/-trip-history-/-idling" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                            <circle id="Oval" fill="#FC8939" cx="12.5" cy="12.5" r="8"></circle>
                            <path d="M11.5161134,17 L8,17 L8,8 L11.5161134,8 L11.5161134,17 Z M17,17 L13.4838866,17 L13.4838866,8 L17,8 L17,17 Z" id="Combined-Shape" fill="#FFFFFF" fill-rule="nonzero" transform="translate(5, 5) scale(0.6)"></path>
                        </g>
                    </svg>`;
            /* tslint:enable */

            case 'idle':
                /* tslint:disable */
                return `<svg width="${options.size}" height="${options.size}" viewBox="0 0 25 25" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
                        <title>pin / trip-history / stopped</title>
                        <g id="pin-/-trip-history-/-stopped" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                            <circle id="Oval" fill="#F44055" cx="12.5" cy="12.5" r="8"></circle>
                            <rect id="Rectangle" fill="#FFFFFF" fill-rule="nonzero" transform="translate(5, 5) scale(0.6) " x="8" y="8" width="9" height="9"></rect>
                        </g>
                    </svg>`;
            /* tslint:enable */

            case 'direction':
                /* tslint:disable */
                return `<svg width="${options.size}" height="${options.size}" viewBox="0 0 25 25" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
                        <title>pin / trip-history / moving</title>
                        <g id="pin-/-trip-history-/-moving" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"
                            transform="rotate(${options.rotation},12.5,12.5)"
                        >
                            <circle id="Oval" fill="#00BE73" cx="12.5" cy="12.5" r="8"></circle>
                            <polygon id="Triangle" fill="#FFFFFF" fill-rule="nonzero" transform="translate(12.500000, 12.00000) scale(0.6) translate(-12.500000, -11.000000) " points="12.5 4 20 18 12.4338882 13.0987632 5 18"></polygon>
                        </g>
                    </svg>`;
            /* tslint:enable */

            case 'non-ignition':
                /* tslint:disable */
                return `<svg width="${options.size}" height="${options.size}" viewBox="0 0 25 25" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
                        <title>pin / trip-history / non-ignition</title>
                        <g id="pin-/-trip-history-/-non-ignition" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                            <circle cx="12.5" cy="12.5" r="8" fill="#161616"/>
                            <circle cx="12.5" cy="12.5" r="4" fill="#FFFFFF"/>
                        </g>
                    </svg>`;
            /* tslint:enable */

            case 'gemini-x':
                /* tslint:disable */
                return `<svg width="${options.size}" height="${options.size}" viewBox="-8 -8 40 40" version="1.1" xmlns="http://www.w3.org/2000/svg">
                        <g clip-path="url(#clip0_1699_4964)">
                            <path d="M0 3.6C0 1.61178 1.61178 0 3.6 0H20.4C22.3882 0 24 1.61178 24 3.6V20.4C24 22.3882 22.3882 24 20.4 24H3.6C1.61178 24 0 22.3882 0 20.4V3.6Z" fill="#FC008E" />
                            <path d="M9.05318 6.59961L6.57163 6.60213C6.08333 6.60263 5.82082 7.17273 6.13948 7.54062L11.9814 14.2851L14.5083 17.2023C14.6168 17.3276 14.7748 17.3996 14.9411 17.3996H17.4278C17.9164 17.3996 18.1794 16.8291 17.8604 16.461L9.48643 6.79682C9.3778 6.67145 9.21959 6.59944 9.05318 6.59961Z" fill="white" />
                            <path d="M17.8605 7.54062C18.1792 7.17273 17.9167 6.60263 17.4284 6.60213L14.9468 6.59961C14.7804 6.59944 14.6222 6.67146 14.5136 6.79682L10 12.0058L12.0186 14.2851L17.8605 7.54062Z" fill="white" />
                            <path d="M11 15.4613L8.99 13.1719L6.13961 16.4611C5.82067 16.8292 6.08365 17.3997 6.57228 17.3997H9.05897C9.22522 17.3997 9.38321 17.3276 9.49171 17.2024L11 15.4613Z" fill="white" />
                        </g>
                        <defs>
                            <clipPath id="clip0_1699_4964">
                                <rect width="24" height="24" fill="white" />
                            </clipPath>
                        </defs>
                    </svg>`;

            default:
                return `<svg></svg>`;
        }
    }
}
